import {
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import LoadingButton from "components/button/LoadingButton";
import DateRangePickerV2 from "components/date-picker/DateRangePickerV2";
import Select from "components/select/Select";
import { LIST_CALL_TYPE } from "constant";
import {
  yupDateRange,
  yupNumberRequired,
  yupStringRequired,
} from "constant/validation";
import { Field, FormikProvider, useFormik } from "formik";
import { isEqual } from "lodash";
import moment from "moment";
import { useState } from "react";
import {
  useCreateCallFlow,
  useEditCallFlow,
} from "service/query/call-flow.query";
import { useGetListProvince } from "service/query/common.query";
import * as Yup from "yup";

import { useListPhoneNumberByContactCenter } from "../../hooks/useListPhoneNumberByContactCenter";
import CallFlowSetupPlan from "./components/CallFlowSetupPlan";

const steps = ["Chọn bộ phận", "Thiết lập kịch bản", "Thời gian áp dụng"];

const yupPlan = Yup.array().of(
  Yup.lazy((values) =>
    Yup.object().shape({
      type: yupStringRequired,
      ...(values.type === "playIvr" && { ivr: yupStringRequired }),
      ...(values.type === "routing" && { queue: yupStringRequired }),
      ...(values.type === "pressKey" && {
        correctKey: Yup.array().of(
          Yup.object().shape({
            key: yupNumberRequired,
            plan: yupPlan,
          })
        ),
        wrongKey: Yup.object().shape({
          plan: yupPlan,
        }),
        notPress: Yup.object().shape({
          pendingTime: yupNumberRequired,
          plan: yupPlan,
        }),
      }),
      ...(values.type === "busy" && {
        pendingTime: yupNumberRequired,
        plan: yupPlan,
      }),
      ...(values.type === "dirvert" && { numberPhone: yupStringRequired }),
    })
  )
);

const yupOnTime = Yup.object().shape({
  detailPlan: yupPlan,
  time: Yup.array().of(
    Yup.object().shape({
      fromDay: Yup.number(),
      toDay: Yup.number(),
      fromTime: Yup.date(),
      toTime: Yup.date(),
    })
  ),
});

const step0Schema = Yup.object().shape({
  contactCenter: yupStringRequired,
  callType: yupStringRequired,
  number: yupStringRequired,
});

const step1Schema = Yup.object().shape({
  onTime: yupOnTime,
  overTime: yupOnTime,
  outTime: yupOnTime,
});

const step2Schema = Yup.object().shape({
  timeApply: Yup.object().shape({
    type: yupStringRequired,
    dateRange: yupDateRange,
  }),
});

// mode view, edit, create
const CallFlowDetail = ({ currentItem, onSuccess, mode }) => {
  const isViewMode = mode === "view";
  const [activeStep, setActiveStep] = useState(0);
  const { data: listProvince } = useGetListProvince({
    filter: { where: { isCskh: true } },
  });
  const createCallFlowQuery = useCreateCallFlow();
  const editCallFlowQuery = useEditCallFlow(currentItem?.id);

  const handleSubmit = (formValues) => {
    const payload = {
      contactCenter: formValues.contactCenter,
      callType: formValues.callType,
      number: formValues.number,
      plan: {
        onTime: formValues.onTime || {},
        overTime: formValues.overTime || {},
        outTime: formValues.outTime || {},
      },
      timeApply: {
        type: formValues.timeApply.type,
        fromDay: moment(formValues.timeApply.dateRange.from)
          .startOf("day")
          .toISOString(),
        endDay: moment(formValues.timeApply.dateRange.to)
          .endOf("day")
          .toISOString(),
      },
    };
    if (currentItem) {
      const isChanged = !isEqual(currentItem, { ...currentItem, ...payload });
      if (isChanged) {
        // eslint-disable-next-line unused-imports/no-unused-vars
        const { id, ...restCurrentItem } = currentItem;
        editCallFlowQuery.mutate(
          {
            ...restCurrentItem,
            ...payload,
          },
          {
            onSuccess,
          }
        );
      } else {
        onSuccess();
      }
    } else {
      createCallFlowQuery.mutate(payload, {
        onSuccess,
      });
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formik = useFormik({
    initialValues: {
      contactCenter: currentItem ? currentItem.contactCenter : "",
      callType: currentItem ? currentItem.callType : "",
      number: currentItem ? currentItem.number : "",

      onTime: currentItem ? currentItem.plan.onTime : {},
      overTime: currentItem ? currentItem.plan.overTime : {},
      outTime: currentItem ? currentItem.plan.outTime : {},

      timeApply: {
        type: currentItem ? currentItem.timeApply.type : "short-term",
        dateRange: {
          from: currentItem ? new Date(currentItem.timeApply.fromDay) : today,
          to: currentItem ? new Date(currentItem.timeApply.endDay) : today,
        },
      },
    },
    onSubmit: handleSubmit,
    validationSchema: step0Schema.concat(step1Schema).concat(step2Schema),
  });

  const handleNext = () => {
    formik.validateForm();
    // validate bước chọn bộ phận
    if (activeStep === 0) {
      try {
        step0Schema.validateSync({
          contactCenter: formik.values.contactCenter,
          callType: formik.values.callType,
          number: formik.values.number,
        });
        setActiveStep(1);
        return;
      } catch (error) {
        return;
      }
    }

    // validate bước thiết lập kịch bản
    if (activeStep === 1) {
      try {
        step1Schema.validateSync({
          onTime: formik.values.onTime,
          overTime: formik.values.overTime,
          outTime: formik.values.outTime,
        });
        setActiveStep(2);
      } catch (error) {
        console.log("error", error);
        return;
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const { listPhoneNumberByContactCenter } = useListPhoneNumberByContactCenter({
    callType: formik.values.callType,
    listProvince,
    contactCenter: formik.values.contactCenter,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box
          mt={3}
          mb={1}
          {...(isViewMode && { style: { pointerEvents: "none" } })}>
          <Grid
            container
            spacing={2}
            sx={{
              ...(activeStep !== 0 && { display: "none" }),
            }}>
            <Grid item xs={12} md={3}>
              <Field name="contactCenter">
                {({ field, meta, form }) => (
                  <Select
                    label="Bộ phận"
                    options={listProvince?.map((item) => ({
                      label: item.name,
                      value: item.code,
                    }))}
                    value={field.value}
                    onChange={async (newValue) => {
                      await form.setFieldValue(field.name, newValue);
                      await form.setFieldValue("number", "");
                    }}
                    error={meta.error}
                  />
                )}
              </Field>
            </Grid>
            <Grid item xs={12} md={3}>
              <Field name="callType">
                {({ field, meta, form }) => (
                  <Select
                    value={field.value}
                    onChange={async (newValue) => {
                      await form.setFieldValue(field.name, newValue);
                      await form.setFieldValue("number", "");
                    }}
                    error={meta.error}
                    label="Loại cuộc gọi"
                    options={LIST_CALL_TYPE}
                    showSearch={false}
                  />
                )}
              </Field>
            </Grid>
            <Grid item xs={12} md={3}>
              <Field name="number">
                {({ field, meta, form }) => (
                  <Select
                    value={field.value}
                    onChange={async (newValue) =>
                      await form.setFieldValue(field.name, newValue)
                    }
                    error={meta.error}
                    label="Đầu số"
                    options={listPhoneNumberByContactCenter?.map((item) => ({
                      label: item,
                      value: item,
                    }))}
                  />
                )}
              </Field>
            </Grid>
          </Grid>

          <Stack
            spacing={2}
            divider={<Divider />}
            sx={{
              ...(activeStep !== 1 && { display: "none" }),
            }}>
            <CallFlowSetupPlan
              title="Trong giờ làm việc"
              formKey="onTime"
              formik={formik}
              mode={mode}
            />
            <CallFlowSetupPlan
              title="Giờ tăng ca"
              formKey="overTime"
              formik={formik}
              mode={mode}
            />
            <CallFlowSetupPlan
              title="Ngoài giờ làm việc"
              formKey="outTime"
              formik={formik}
              mode={mode}
            />
          </Stack>

          <Box
            sx={{
              ...(activeStep !== 2 && { display: "none" }),
            }}>
            <Typography fontStyle="italic">
              <Typography color="red" component="span">
                Chú ý:{" "}
              </Typography>
              <Typography component="span">
                Chọn thời gian áp dụng sớm nhất là 2 ngày so với hiện tại
              </Typography>
            </Typography>

            <FormControl>
              <RadioGroup
                value={formik.values.timeApply?.type}
                onChange={(e) => {
                  formik.setFieldValue("timeApply.type", e.target.value);
                }}
                sx={{ display: "block" }}>
                <Box>
                  <FormControlLabel
                    value="short-term"
                    control={<Radio />}
                    label="Ngắn hạn"
                  />

                  <DateRangePickerV2
                    value={formik.values.timeApply.dateRange}
                    onChange={(newValue) => {
                      formik.setFieldValue("timeApply.dateRange", newValue);
                    }}
                    error={formik.errors?.timeApply?.dateRange}
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    value="long-term"
                    control={<Radio />}
                    label="Dài hạn"
                  />
                </Box>
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            pt: 2,
            justifyContent: "flex-end",
          }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 2 }}>
            Quay lại
          </Button>
          {activeStep < steps.length - 1 && (
            <Button
              onClick={handleNext}
              variant="contained"
              sx={{ width: 100 }}>
              Tiếp theo
            </Button>
          )}
          {activeStep === steps.length - 1 && !isViewMode && (
            <LoadingButton
              variant="contained"
              type="submit"
              sx={{ width: 100 }}
              loading={
                createCallFlowQuery.isPending || editCallFlowQuery.isPending
              }>
              Lưu
            </LoadingButton>
          )}
        </Box>
      </FormikProvider>
    </form>
  );
};

export default CallFlowDetail;
